export type TValues = number[][];

class Point {
  x: number;
  y: number;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }
}

class Size {
  w: number;
  h: number;

  constructor(w: number, h: number) {
    this.w = w;
    this.h = h;
  }
}

class Label {
  value: number;
  text: string;

  constructor(value: number, text: string) {
    this.value = value;
    this.text = text;
  }
}

const maxOf = (values: TValues): number => {
  let maxValue = Number.MIN_SAFE_INTEGER;
  for (let i = 0; i < values.length; i++) {
    const innerValues = values[i].filter((v) => v !== null);
    maxValue = Math.max(maxValue, Math.max(...innerValues));
  }
  return maxValue;
};

const minAndMaxOf = (values: TValues): [number, number] => {
  let min = Number.MAX_SAFE_INTEGER;
  let max = Number.MIN_SAFE_INTEGER;
  for (let i = 0; i < values.length; i++) {
    const innerValues = values[i].filter((v) => v !== null);
    min = Math.min(min, Math.min(...innerValues));
    max = Math.max(max, Math.max(...innerValues));
  }
  return [Math.floor(min / 5) * 5, Math.ceil(max / 5) * 5];
};

const totalMinAndMaxOf = (valuesList: TValues[]): [number, number] => {
  let minValue = Number.MAX_SAFE_INTEGER;
  let maxValue = Number.MIN_SAFE_INTEGER;
  valuesList.forEach((values) => {
    const [localMin, localMax] = minAndMaxOf(values);
    minValue = Math.min(minValue, localMin);
    maxValue = Math.max(maxValue, localMax);
  });
  return [minValue, maxValue];
};

const getTickLabels = (
  minValue: number,
  maxValue: number,
  step: number,
  unit: string,
): Label[] => {
  const numOfTicks = Math.round((maxValue - minValue) / step) + 1;
  const labelDigits = maxValue >= 3 ? 0 : maxValue >= 0.5 ? 1 : 2;
  let labels = Array(numOfTicks);
  for (let i = 0; i < numOfTicks; i++) {
    const value = minValue + i * step;
    labels[i] = new Label(value, value.toFixed(labelDigits) + unit);
  }
  return labels;
};

export const getMaxAndStep = (maxValue: number) => {
  const configs = [
    [0.2, 0.04],
    [0.3, 0.1],
    [0.4, 0.1],
    [0.5, 0.1],
    [1.0, 0.2],
  ];
  const factors = [1, 10, 100, 1000, 10000];
  for (const factor of factors) {
    for (const config of configs) {
      const maxConfigValue = config[0] * factor;
      if (maxValue <= maxConfigValue) {
        return config.map((x) => x * factor);
      }
    }
  }
  return [0, 0];
};

export const minMaxStepConfigs: [number, number, number][] = [
  [-20, -10, 5],
  [-20, 0, 5],
  [-20, 10, 10],
  [-20, 20, 10],
  [-20, 30, 10],
  [-10, 0, 5],
  [-10, 10, 5],
  [-10, 20, 10],
  [-10, 30, 10],
  [0, 3, 1],
  [0, 10, 5],
  [0, 20, 5],
  [0, 30, 10],
  [0, 40, 10],
  [10, 20, 5],
  [10, 30, 5],
  [10, 40, 10],
  [15, 20, 1],
  [15, 25, 5],
  [20, 25, 1],
  [20, 30, 5],
  [20, 35, 5],
  [20, 40, 5],
  [25, 30, 1],
  [30, 35, 1],
  [30, 40, 5],
  [950, 1050, 25],
];

export const getMinAndMaxAndStep = (minValue: number, maxValue: number) => {
  const possibleConfigs = minMaxStepConfigs.filter(
    (config) => minValue >= config[0] && maxValue <= config[1],
  );
  const sortedPossibleConfigs = possibleConfigs.sort(
    (a, b) => a[1] - a[0] - (b[1] - b[0]),
  );
  return sortedPossibleConfigs.length > 0
    ? sortedPossibleConfigs[0]
    : [0, 0, 0];
};

export const calcY = (
  value: number,
  minValue: number,
  valueRange: number,
  height: number,
): number => {
  return height - ((value - minValue) / valueRange) * height;
};

const Padding = 2;

export class MeasurementChartPainter {
  private width: number;
  private height: number;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  drawPercentBars(ctx: CanvasRenderingContext2D, values: TValues) {
    const numOfBars = values.length;
    const [chartPoint, chartSize] = this.drawChart(ctx, numOfBars);
    const labels = [
      new Label(0, "0%"),
      new Label(25, "25%"),
      new Label(50, "50%"),
      new Label(75, "75%"),
      new Label(100, "100%"),
    ];
    this.drawYLabelsAndYTickLines(ctx, labels, chartPoint, chartSize);
    const grd = ctx.createLinearGradient(0, 0, 0, chartSize.h);
    grd.addColorStop(0.75, "#00aa00ff");
    grd.addColorStop(1, "#ff0000ff");
    ctx.fillStyle = grd;
    this.drawValuesAsBars(ctx, values, chartPoint, chartSize, 100);
  }

  drawBars(
    ctx: CanvasRenderingContext2D,
    values: TValues,
    color: string,
    unit: string,
  ) {
    const numOfBars = values.length;
    const [chartPoint, chartSize] = this.drawChart(ctx, numOfBars);
    const [maxValue, step] = getMaxAndStep(maxOf(values));
    const labels = getTickLabels(0, maxValue, step, unit);
    this.drawYLabelsAndYTickLines(ctx, labels, chartPoint, chartSize);
    ctx.fillStyle = color;
    this.drawValuesAsBars(ctx, values, chartPoint, chartSize, maxValue);
  }

  drawLines(
    ctx: CanvasRenderingContext2D,
    valuesList: TValues[],
    colorList: string[],
    unit: string,
    minAndMaxAndStep: number[] = [],
  ) {
    const numOfBars = valuesList[0].length;
    const [chartPoint, chartSize] = this.drawChart(ctx, numOfBars);
    const [minValue, maxValue, step] =
      minAndMaxAndStep.length === 3
        ? minAndMaxAndStep
        : getMinAndMaxAndStep(...totalMinAndMaxOf(valuesList));
    const labels = getTickLabels(minValue, maxValue, step, unit);
    this.drawYLabelsAndYTickLines(ctx, labels, chartPoint, chartSize);
    this.drawValuesAsLines(
      ctx,
      valuesList,
      colorList,
      chartPoint,
      chartSize,
      minValue,
      maxValue,
    );
  }

  private drawChart(
    ctx: CanvasRenderingContext2D,
    numOfBars: number,
  ): [Point, Size] {
    const paddingTop = 6;
    const paddingBottom = 2;
    const xLabelsOffset = 30;
    const yLabelsOffset = 10;
    const barWidth = 24;
    const chartPoint = new Point(
      xLabelsOffset + (this.width - numOfBars * barWidth - xLabelsOffset) / 2,
      paddingTop,
    );
    const chartSize = new Size(
      numOfBars * 24,
      this.height - paddingTop - paddingBottom - yLabelsOffset - Padding,
    );

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, this.width, this.height);

    ctx.fillStyle = "#eeeeee";
    for (let i = 0; i < numOfBars; i++) {
      if (i % 2 === 0) {
        ctx.fillRect(
          chartPoint.x + i * barWidth,
          chartPoint.y,
          barWidth,
          chartSize.h,
        );
      }
    }

    ctx.strokeStyle = "#cccccc";
    ctx.lineWidth = 1;
    ctx.strokeRect(chartPoint.x, chartPoint.y, chartSize.w, chartSize.h);

    ctx.fillStyle = "#000000";
    ctx.font = "12px Arial";
    const dayLabels = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
    const today = new Date();
    const dayIndex = today.getDay();
    const dayIndexOffset = dayIndex + 7 - (numOfBars % 7);
    const y = chartPoint.y + chartSize.h + yLabelsOffset + Padding;
    for (let i = 0; i < numOfBars; i++) {
      const dayLabel = dayLabels[(i + dayIndexOffset) % 7];
      const textBox = ctx.measureText(dayLabel);
      const xOffset = (barWidth - textBox.width) / 2.0;
      ctx.fillText(dayLabel, chartPoint.x + i * barWidth + xOffset, y);
    }

    return [chartPoint, chartSize];
  }

  private drawValuesAsBars(
    ctx: CanvasRenderingContext2D,
    values: TValues,
    chartPoint: Point,
    chartSize: Size,
    maxValue: number,
  ) {
    for (let i = 0; i < values.length; i++) {
      const valuesPerDay = values[i];
      const xOffset = chartPoint.x + 24 * i;
      for (let h = 0; h < 24; h++) {
        const value = valuesPerDay[h];
        if (value > 0) {
          const x = xOffset + h;
          const valueOffset = (chartSize.h * (maxValue - value)) / maxValue;
          const y = chartPoint.y + valueOffset;
          ctx.fillRect(x, y, 1, chartSize.h - valueOffset);
        }
      }
    }
  }

  private drawValuesAsLines(
    ctx: CanvasRenderingContext2D,
    valuesList: TValues[],
    colorList: string[],
    chartPoint: Point,
    chartSize: Size,
    minValue: number,
    maxValue: number,
  ) {
    const valueRange = maxValue - minValue;
    valuesList.forEach((values, index) => {
      const color = colorList.at(index);
      ctx.strokeStyle = color || "#000000";
      ctx.lineWidth = 1;
      ctx.beginPath();
      let lastValue = null;
      for (let i = 0; i < values.length; i++) {
        const valuesPerDay = values[i];
        const xOffset = chartPoint.x + 24 * i;
        for (let h = 0; h < 24; h++) {
          if (valuesPerDay[h] !== null) {
            const x = xOffset + h;
            const y =
              chartPoint.y +
              calcY(valuesPerDay[h], minValue, valueRange, chartSize.h);
            if (lastValue !== null) {
              ctx.lineTo(x, y);
            } else {
              ctx.moveTo(x, y);
            }
          }
          lastValue = valuesPerDay[h];
        }
      }
      ctx.stroke();
    });
  }

  private drawYLabelsAndYTickLines(
    ctx: CanvasRenderingContext2D,
    labels: Label[],
    chartPoint: Point,
    chartSize: Size,
  ) {
    ctx.lineWidth = 1;
    const yOffset = chartSize.h / (labels.length - 1);
    labels.forEach((label, index) => {
      const textBox = ctx.measureText(label.text);
      const x = chartPoint.x - textBox.width - Padding;
      const y = chartPoint.y + chartSize.h - yOffset * index;
      ctx.fillText(label.text, x, y + textBox.actualBoundingBoxAscent / 2);
      if (index !== 0 && index !== labels.length - 1) {
        ctx.strokeStyle = label.value === 0 ? "#bbbbbb" : "#dddddd";
        ctx.setLineDash(label.value === 0 ? [] : [1, 1]);
        ctx.beginPath();
        ctx.moveTo(chartPoint.x, y);
        ctx.lineTo(chartPoint.x + chartSize.w, y);
        ctx.stroke();
      }
    });
    ctx.setLineDash([]);
  }
}
